import { createApp, ref } from 'vue';
import '@/styles/index.js';
import { createPinia } from 'pinia';
import 'remixicon/fonts/remixicon.css';
import { createI18n } from 'vue-i18n';
import es from '@/locales/es.json';
import en from '@/locales/en.json';
import PageLayout from '@/layouts/PageLayout.vue';
import ApplicationInsightsService from '@/services/applicationInsightsService';

// Create a single global Pinia instance to share state across all Vue apps
if (!window.__GLOBAL_PINIA__) {
  window.__GLOBAL_PINIA__ = createPinia();
}

// Create a single global i18n instance to share locale across all Vue apps
function getGlobalI18n() {
  if (!window.__GLOBAL_I18N__) {
    const urlParams = new URLSearchParams(window.location.search);
    const urlLang = urlParams.get("language") || urlParams.get("lang");
    const defaultLang = ["en", "es"].includes(urlLang || "") ? urlLang : "en";
    window.__GLOBAL_I18N__ = createI18n({
      legacy: false,
      locale: defaultLang,
      fallbackLocale: 'en',
      messages: { en, es },
    });
  }
  return window.__GLOBAL_I18N__;
}

const devtools = window.__VUE_DEVTOOLS_GLOBAL_HOOK__;
// Multiple Vue entry bundles can load on one page, so they share one telemetry client and console wrapper.
const applicationInsightsState = window.__ROOF_HUB_APPLICATION_INSIGHTS__ ||= {
  client: null,
  initialized: false,
  originalConsoleError: null,
};
// Local-only switch: defaults off. Non-local hosts always capture browser telemetry.
const ENABLE_LOCAL_BROWSER_TELEMETRY = false;

function shouldCaptureBrowserTelemetry() {
  const localHostnames = ['localhost', '127.0.0.1', '::1'];
  return !localHostnames.includes(window.location.hostname) || ENABLE_LOCAL_BROWSER_TELEMETRY;
}

export function bootstrapApp(App, page) {
  const pinia = window.__GLOBAL_PINIA__;

  const config = ref({ ...(window.appConfig || {}) });
  if (shouldCaptureBrowserTelemetry()) {
    ApplicationInsightsService.initialize(applicationInsightsState, config.value.applicationInsightsConnectionString);
  }

  // `window.appConfig` is set by server-rendered Razor views. Depending on script order,
  // it may be assigned/mutated after the Vue app is mounted. Because those mutations
  // happen outside Vue reactivity, we periodically resync into the injected `config`.
  let lastConfigJson = null;
  const syncConfigFromWindow = () => {
    try {
      const current = window.appConfig || {};
      const json = JSON.stringify(current);
      if (json !== lastConfigJson) {
        lastConfigJson = json;
        config.value = { ...current };
      }
    } catch {
      // ignore serialization issues; keep last known config
    }
  };

  syncConfigFromWindow();
  const syncInterval = setInterval(syncConfigFromWindow, 250);
  // Stop syncing after a short period to avoid an always-on timer.
  setTimeout(() => clearInterval(syncInterval), 5000);
  
  // Use the shared global i18n instance
  const i18n = getGlobalI18n();
  
  const app = createApp({
    name: 'Host',
    ...App,
  });
  app.use(pinia);
  app.use(i18n);
  app.provide('config', config);
  app.config.errorHandler = (error, _instance, info) => ApplicationInsightsService.captureVueError(applicationInsightsState, error, info);
  const version = app.version;
  app.component('PageLayout', PageLayout)
  if (devtools) {
    devtools.enabled = config.value?.devToolsEnabled;
    if (config.value?.devToolsEnabled) {
      devtools.emit('app:init', app, version, {});
    }
  }
  app.mount('#' + (page || 'app'));
}